import { FormEvent, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { loginResponseMessage } from '@/lib/loginPath';
import { resolvePostLoginTarget } from '@/lib/mobileAdminPaths';
import { useSession } from '@/hooks/useSession';
import { AppVersionLabel } from '@/components/AppVersionLabel';
import { APP_BRAND_TITLE } from '@/lib/appVersion';

type LoginResp = { ok: boolean; err?: string; detail?: string };

export function MobileLoginPage() {
  const { ok, loading } = useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('session') === 'expired';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    try {
      const r = await apiFetch<LoginResp>('login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
      if (r.ok) {
        navigate(resolvePostLoginTarget(searchParams), { replace: true });
      } else {
        const msg =
          r.err === 'account_expired' && r.detail
            ? r.detail
            : loginResponseMessage(r.err);
        setErr(msg);
      }
    } catch {
      setErr('网络异常，请检查网络或稍后再试');
    } finally {
      setSubmitting(false);
    }
  }

  if (!loading && ok) {
    return <Navigate to={resolvePostLoginTarget(searchParams)} replace />;
  }

  return (
    <div
      className="relative flex min-h-screen flex-col overflow-hidden bg-slate-950"
      style={{
        paddingTop: 'max(0.75rem, env(safe-area-inset-top))',
        paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
      }}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -left-1/4 top-0 h-[420px] w-[150%] rounded-b-[50%] bg-gradient-to-b from-teal-500/30 via-teal-600/10 to-transparent blur-2xl" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-500/20 to-transparent" />
      </div>

      <header className="relative z-10 flex shrink-0 items-center justify-between px-4 pt-2">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-icon text-xl text-white shadow-lg shadow-teal-950/50 ring-1 ring-white/10">
            <i className="fa-solid fa-shrimp" aria-hidden />
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-lg font-bold tracking-tight text-white">{APP_BRAND_TITLE}</h1>
            <p className="truncate text-xs text-slate-400">信息后台 · 手机登录</p>
          </div>
        </div>
        <Link
          to="/"
          className="shrink-0 rounded-full px-3 py-2 text-xs font-medium text-teal-200/90 ring-1 ring-white/10 transition hover:bg-white/5 hover:text-white"
        >
          首页
        </Link>
      </header>

      <main className="relative z-10 mt-6 flex flex-1 flex-col px-4">
        <div className="mx-auto w-full max-w-md flex-1 rounded-t-[1.75rem] border border-slate-200/60 border-b-0 bg-white/98 px-5 pb-8 pt-7 shadow-[0_-12px_48px_rgba(0,0,0,0.4)] backdrop-blur-sm">
          <p className="text-center text-[15px] font-semibold text-slate-900">登录账号</p>
          <p className="mt-1 text-center text-xs leading-relaxed text-slate-500">
            与电脑登录为同一账号。系统管理员与普通用户共用此入口；复杂配置与审计建议在电脑浏览器操作。
          </p>

          {sessionExpired ? (
            <p className="mt-4 rounded-xl bg-teal-50 px-3 py-2.5 text-center text-sm text-teal-950">
              登录已过期，请重新登录。
            </p>
          ) : null}

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600" htmlFor="m-login-user">
                用户名
              </label>
              <input
                id="m-login-user"
                className="min-h-12 w-full rounded-xl border border-slate-200 bg-slate-50/80 px-4 text-base text-slate-900 outline-none ring-teal-600/0 transition placeholder:text-slate-400 focus:border-teal-500 focus:bg-white focus:ring-4 focus:ring-teal-500/15"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                inputMode="text"
                enterKeyHint="next"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600" htmlFor="m-login-pass">
                密码
              </label>
              <input
                id="m-login-pass"
                type="password"
                className="min-h-12 w-full rounded-xl border border-slate-200 bg-slate-50/80 px-4 text-base text-slate-900 outline-none ring-teal-600/0 transition placeholder:text-slate-400 focus:border-teal-500 focus:bg-white focus:ring-4 focus:ring-teal-500/15"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                enterKeyHint="go"
              />
            </div>

            {err ? (
              <p className="rounded-xl bg-rose-50 px-3 py-2.5 text-center text-sm text-rose-800" role="alert">
                {err}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="flex min-h-12 w-full items-center justify-center rounded-xl bg-gradient-to-b from-teal-500 to-teal-600 text-base font-semibold text-white shadow-lg shadow-teal-900/30 ring-1 ring-teal-500/25 transition active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? '登录中…' : '登 录'}
            </button>
          </form>

          <div className="mt-8 space-y-3 border-t border-slate-100 pt-6 text-center text-xs text-slate-500">
            <p>
              用电脑登录？
              <Link to="/login" className="ml-1 font-semibold text-teal-700 underline underline-offset-2">
                切换电脑版登录
              </Link>
            </p>
            <p>
              <AppVersionLabel className="text-slate-400" />
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
